/* eslint-disable class-methods-use-this */
import {
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  firstValueFrom,
  map,
  merge,
  Observable,
  of,
  skip,
  startWith,
  Subscription,
  take,
} from 'rxjs';
import {defineMessages} from 'react-intl';
import isEqual from 'fast-deep-equal/react';
import {v4 as uuid} from 'uuid';

import {
  AbstractClient,
  ButtonOptionsConversationMessage,
  Client,
  ClientErrorResponse,
  ClientResponse,
  ClientUrls,
  Conversation,
  ConversationAction,
  ConversationActionParams,
  ConversationArticle,
  ConversationChannel,
  ConversationContext,
  ConversationLogItem,
  ConversationLogItemMessageAdded,
  ConversationMessage,
  ConversationParticipant,
  ConversationRequestAction,
  ConversationStatus,
  ConversationSyncStorageMode,
  GetConversationMessagesParams,
  GetConversationMessagesResponse,
  GetConversationParticipantsParams,
  GetConversationParticipantsResponse,
  InlineDataLogItem,
  InProgressData,
  InProgressParams,
  InProgressResponse,
  LeaveConversationParams,
  MessagePartActionParams,
  MessagePartSubmitParams,
  ObservabilityConfig,
  RequestAction,
  SelectOptionAction,
  SelectOptionsConversationMessage,
  SendConversationMessageParams,
  SendConversationTypingParams,
  SendFeedbackParams,
  StartConversationParams,
  StartConversationResponse,
  StreamTextParams,
  StreamTextResponse,
  TriggerParams,
} from '@stripe-internal/conversations-client';
import {InlineDataEventBus} from '@stripe-internal/inline-data';
import {UniversalHelpUser} from '@stripe-internal/universal-help-conversations-client';

import {Api} from './api/api';
import {AppConfig, AppFeatureFlags, AppProfile} from '../types';
import {devLog} from './util/dev';
import {
  isDotComConversation,
  shouldContextRequestPageContext,
  shouldConversationSendPageContext,
} from './util/conversationUtils';
import {METRIC} from './util/clientMetrics';

export const messages = defineMessages({
  closeChatConfirmation: {
    id: 'conversations_assistant.support_conversations_client.closeChatConfirmation.closeChatDescription',
    description:
      'Instruction provided when user intends to close the chat window',
    defaultMessage: 'Are you sure you want to end this chat?',
  },
  closeChatButtonText: {
    id: 'conversations_assistant.support_conversations_client.closeChatConfirmation.closeChatButtonText',
    description:
      'Text shown for the close chat button when closing support chat window',
    defaultMessage: 'End the chat',
  },
  chatEndedMessage: {
    id: 'conversations_assistant.support_conversations_client.chatEndedMessage',
    description:
      'Message to show to prompt user to close chat widget to start new chat',
    defaultMessage:
      'For further assistance, please close the window and start a new chat.',
  },
  newConversationPrompt: {
    id: 'conversations_assistant.support_conversations_client.newConversationPrompt',
    description:
      'Message to show to prompt user to enter a message to start a chat',
    defaultMessage: 'Send a message to let us know how we can help.',
  },
  waitingForAgent: {
    id: 'conversations_assistant.support_conversations_client.waitingForAgent',
    description:
      'Message to show to prompt user to enter a message to start a chat',
    defaultMessage: 'Waiting for an agent to join.',
  },
  transitionToAgent: {
    id: 'conversations_assistant.support_conversations_client.transitionToAgent',
    description:
      'Message to show to prompt user when transitioning chat to an agent',
    defaultMessage: 'Connecting you with an agent.',
  },
  transitionToAgentDotCom: {
    id: 'conversations_assistant.support_conversations_client.transitionToAgentDotCom',
    description:
      'Message to show to prompt user when transitioning chat to an agent for dot com',
    defaultMessage: 'Adding an agent to the chat.',
  },
  transitionToAgentV2: {
    id: 'conversations_assistant.support_conversations_client.transitionToAgentV2',
    description:
      'Message to show to prompt user when transitioning chat to an agent',
    defaultMessage: 'Connecting you to a support agent.',
  },
});

export const UHLiveChannelsClientType = 'support_conversations_client';
const METRIC_TAGS = {client_type: UHLiveChannelsClientType};
const ERROR_TAGS = METRIC_TAGS;

const INTERNAL_TEST_STRING = /^\s*stripe\s+internal\s+test\s*$/i;
const INTERNAL_SYNTHETIC_TEST_STRING =
  /^\s*stripe\s+internal\s+synthetic\s+test\s*$/i;
const INTERNAL_SYNTHETIC_END_CHAT_TEST_STRING =
  /^\s*stripe\s+internal\s+synthetic\s+test\s+-\s+end\s+chat\s*$/i;
const INTERRUPTABLE_INLINE_DATA_NAMES = ['live_answers_response'];

const MAX_END_LIVE_CHAT_ATTEMPTS = 3;
const END_LIVE_CHAT_RETRY_DELAY_MS = 250;

export interface UniversalHelpConversationContext {
  articlesVisited?: Record<string, ConversationArticle>;
}

type StartInProgressConversationParams = StartConversationParams & {
  inProgressResponse?: InProgressResponse;
};

type ResumableLiveSupportChannel = Extract<
  ConversationChannel,
  'chat' | 'request_a_call'
>;

type StartUHConversationParams = StartConversationParams & {
  conversationId?: string;
  channelConversationId?: string;
  participant: ConversationParticipant;
};

// resume-only: fresh chats are created by transitionToChat, so the chat being
// resumed always has an existing backend conversation to name
type ResumeChatConversationParams = StartConversationParams & {
  conversationId: string;
  channelConversationId?: string;
  participant: ConversationParticipant;
};

type StartRACConversationParams = StartConversationParams & {
  conversationId?: string;
  participant: ConversationParticipant;
  status?: ConversationStatus;
};

// what `startConversation` resolved a request to, before any of it is acted on
type StartMode =
  | {type: 'reuse'; conversation: Conversation}
  | {type: 'bot'; params: StartUHConversationParams}
  | {type: 'chat'; params: ResumeChatConversationParams}
  | {type: 'request_a_call'; params: StartRACConversationParams};

export interface AssistantClient
  extends Omit<
    Client,
    'getConversationParticipants' | 'sendConversationTyping'
  > {
  getConversationParticipants: () => Promise<GetConversationParticipantsResponse>;
  sendConversationTyping: () => Promise<ClientResponse>;
  syncStorageMode: ConversationSyncStorageMode;
  waitForPendingMessagesHandled: () => Promise<boolean>;
  busyObservable: () => Observable<unknown>;
  isPending: () => boolean;
  actOnMessagePart?: (
    params: MessagePartActionParams,
  ) => Promise<ClientResponse>;
  submitMessagePart?: (
    params: MessagePartSubmitParams,
  ) => Promise<ClientResponse>;
}

export class SupportConversationsClient
  extends AbstractClient
  implements Client
{
  private getConfig: () => AppConfig;

  private getProfile: () => AppProfile;

  private getApi: () => Api;

  private uhClient: AssistantClient;

  private chatClient: Client;

  private inlineDataEventBus: InlineDataEventBus;

  private requestAction: (action: RequestAction) => Promise<ClientResponse>;

  private getCurrentConversation?: () => Conversation | null | undefined;

  private getSyncStorageModeForChannel: (
    channel: ConversationChannel,
    defaultSyncStorageMode: ConversationSyncStorageMode,
  ) => ConversationSyncStorageMode;

  private enableCrossTabChatTransitionSync: boolean;

  private localConversation?: Conversation | null;

  private conversationSub?: Subscription | null;

  private currentChannel: ConversationChannel = 'bot';

  private clientStatusSub?: Subscription | null;

  private lifecycleSub = new Subscription();

  private nextTriggerQuery?: string;

  // Track if the initial greeting has been shown across conversation instances
  private initialGreetingShown = false;

  private stuckConversationRequests = new WeakMap<Conversation, number>();

  private endChatConversationOnLeave: boolean;

  private serverEndsUhConversationOnTransition: boolean;

  constructor({
    getConfig,
    getProfile,
    getApi,
    getObservability,
    urls,
    uhClient,
    chatClient,
    inlineDataEventBus,
    requestAction,
    getCurrentConversation,
    enableCrossTabChatTransitionSync,
    getSyncStorageModeForChannel = (_channel, defaultSyncStorageMode) =>
      defaultSyncStorageMode,
    endChatConversationOnLeave = false,
    serverEndsUhConversationOnTransition = false,
  }: {
    getConfig: () => AppConfig;
    getProfile: () => AppProfile;
    getApi: () => Api;
    getObservability?: () => ObservabilityConfig;
    urls?: Partial<ClientUrls>;
    uhClient: AssistantClient;
    chatClient: Client;
    inlineDataEventBus: InlineDataEventBus;
    requestAction: (action: RequestAction) => Promise<ClientResponse>;
    getCurrentConversation?: () => Conversation | null | undefined;
    enableCrossTabChatTransitionSync: boolean;
    getSyncStorageModeForChannel?: (
      channel: ConversationChannel,
      defaultSyncStorageMode: ConversationSyncStorageMode,
    ) => ConversationSyncStorageMode;
    // Threads-only: when leaving a chat conversation, also end it on the
    // backend via uhClient.leaveConversation (routed to the all-in-one end
    // endpoint), in addition to the Twilio-side chatClient leave below. Only
    // set when enableConversationsThreads is on (see buildV2Clients.ts).
    endChatConversationOnLeave?: boolean;
    // Threads-only: the backend ends the assistant conversation itself once a
    // transition to chat or RAC completes, so there's nothing left to leave
    // locally in fetchLatestAndLeaveUHConversation. Gated on which client
    // builder constructed this instance (buildV2Clients.ts), not just
    // enableConversationsThreads directly — the flag can be on for a client
    // still built via buildV1Clients (legacy endpoints, no server-side end),
    // which must still run the local leave.
    serverEndsUhConversationOnTransition?: boolean;
  }) {
    super({
      clientFetch: getApi().fetch,
      urls,
      observability: getObservability?.(),
    });

    this.getConfig = getConfig;
    this.getProfile = getProfile;
    this.getApi = getApi;

    this.inlineDataEventBus = inlineDataEventBus;

    this.requestAction = requestAction;
    this.getCurrentConversation = getCurrentConversation;
    this.enableCrossTabChatTransitionSync = enableCrossTabChatTransitionSync;
    this.getSyncStorageModeForChannel = getSyncStorageModeForChannel;
    this.endChatConversationOnLeave = endChatConversationOnLeave;
    this.serverEndsUhConversationOnTransition =
      serverEndsUhConversationOnTransition;

    this.uhClient = uhClient;
    this.chatClient = chatClient;

    const clientExceptions = merge(
      this.uhClient
        .eventsObservable()
        .pipe(
          filter(
            (e) =>
              e.name === 'client-exception' &&
              this.currentChannel !== 'chat' &&
              this.currentChannel !== 'request_a_call',
          ),
        ),
      this.chatClient
        .eventsObservable()
        .pipe(filter((e) => e.name === 'client-exception')),
    );

    const conversationExceptions = merge(
      this.uhClient
        .conversationEventsObservable()
        .pipe(
          filter(
            (e) =>
              e.name === 'conversation-exception' &&
              this.currentChannel !== 'chat' &&
              this.currentChannel !== 'request_a_call',
          ),
        ),
      this.chatClient
        .conversationEventsObservable()
        .pipe(filter((e) => e.name === 'conversation-exception')),
    );

    this.lifecycleSub.add(clientExceptions.subscribe((e) => this.emitEvent(e)));
    this.lifecycleSub.add(
      conversationExceptions.subscribe((e) => this.emitConversationEvent(e)),
    );

    this.subscribeToClientStatus(this.uhClient);
  }

  get api() {
    return this.getApi();
  }

  get config() {
    return this.getConfig();
  }

  get profile() {
    return this.getProfile();
  }

  // this allows the the client to be used with threads where the current
  // conversation is managed by the assistant and can change over time, but
  // still preserving the original locally set conversation behavior.
  private get conversation(): Conversation | null | undefined {
    const currentConversation = this.getCurrentConversation?.();
    return currentConversation === undefined
      ? this.localConversation
      : currentConversation;
  }

  private set conversation(conversation: Conversation | null | undefined) {
    this.localConversation = conversation;
  }

  async startConversation(
    params: StartInProgressConversationParams,
  ): Promise<StartConversationResponse> {
    const initialChannel = this.currentChannel;
    try {
      if (this.conversation && params.context?.placeholderConversation) {
        this.clearCurrentConversation();
      }

      const mode = await this.resolveStartMode(params);
      switch (mode.type) {
        case 'reuse':
          return this.reuseCurrentConversation(mode.conversation);
        case 'chat':
          return await this.resumeChatConversation(mode.params);
        case 'request_a_call':
          return await this.startRACConversation(mode.params);
        case 'bot':
        default:
          return await this.startUHConversation(mode.params);
      }
    } catch (e) {
      this.currentChannel = initialChannel;
      const clientErrorResponse = await this.reportAndEmitException(
        `exception starting conversation: ${(e as Error).message}`,
      );
      return clientErrorResponse;
    }
  }

  // Decides which conversation `startConversation` should produce, and with
  // what arguments, without touching client state. Four outcomes: reuse the
  // conversation already started here, resume one of the two live-support
  // channels from backend state, or (re)start on the bot channel. Throws
  // 'Conversation already started' when the request can't be reconciled with
  // the conversation this client already owns.
  private async resolveStartMode(
    params: StartInProgressConversationParams,
  ): Promise<StartMode> {
    const {conversationId: paramsConversationId, participant, context} = params;
    const current = this.conversation;

    if (current) {
      if (paramsConversationId && paramsConversationId !== current.id) {
        throw new Error('Conversation already started');
      }

      const requestedChannel = context?.channel;
      const staleChannel =
        requestedChannel != null &&
        requestedChannel !== current.context.channel;
      if (!staleChannel) {
        return {type: 'reuse', conversation: current};
      }
      if (!paramsConversationId) {
        throw new Error('Conversation already started');
      }
      // the same conversation is being requested on a different channel: it
      // escalated elsewhere (e.g. another tab started a live chat), so the
      // current conversation object is stale and resolution continues below.
      // It stays connected until its live-channel replacement has loaded.
    }

    if (!participant) {
      throw new Error('Participant is required');
    }

    const channelConversationId = context
      ? (context[`${context.channel}_conversationId`] as string | undefined)
      : undefined;
    let conversationId = paramsConversationId;

    const inProgress = await this.resolveInProgress(params);
    if (inProgress?.inProgress) {
      const {channel} = inProgress;
      conversationId = inProgress.conversationId;
      if (channel === 'bot') {
        return {
          type: 'bot',
          params: {
            ...params,
            conversationId,
            channelConversationId: conversationId,
            participant,
          },
        };
      }

      if (channel === 'chat' && conversationId) {
        return {
          type: 'chat',
          params: {
            ...params,
            conversationId,
            channelConversationId: channelConversationId || conversationId,
            participant,
            fetch: true,
          },
        };
      }

      if (channel === 'request_a_call') {
        return {
          type: 'request_a_call',
          params: {...params, conversationId, participant},
        };
      }
    }

    // Historical live-support conversations are intentionally no longer
    // "in progress", but their channel ids still route transcript loads.
    if (this.featureFlags.enableConversationsThreadsHistory && conversationId) {
      if (context?.channel === 'chat' && channelConversationId) {
        return {
          type: 'chat',
          params: {
            ...params,
            conversationId,
            channelConversationId,
            participant,
            fetch: true,
          },
        };
      }
      if (context?.channel === 'request_a_call') {
        return {
          type: 'request_a_call',
          params: {
            ...params,
            conversationId,
            participant,
            status: ConversationStatus.Inactive,
          },
        };
      }
    }

    // Nothing in progress on any channel, so start a new UH conversation and
    // ignore the requested conversationId.
    return {
      type: 'bot',
      params: {...params, conversationId: undefined, participant},
    };
  }

  // The in-progress lookup the start mode is resolved against: whatever the
  // caller already has, otherwise a fresh lookup. Undefined when the lookup
  // was skipped or failed — neither is fatal, resolution continues without it.
  private async resolveInProgress(
    params: StartInProgressConversationParams,
  ): Promise<InProgressData | undefined> {
    let {inProgressResponse} = params;
    if (!inProgressResponse && params.skipInProgress !== true) {
      inProgressResponse = await this.inProgress(params);
    }

    if (!inProgressResponse || 'error' in inProgressResponse) {
      return undefined;
    }

    return inProgressResponse.data;
  }

  // if startConversation gets called twice in succession, and we've already
  // started a conversation, just return it.
  private reuseCurrentConversation(
    conversation: Conversation,
  ): StartConversationResponse {
    const {channel} = conversation.context;
    if (channel) {
      this.currentChannel = channel;
    }
    this.listenForConversationEvents(conversation);

    return {code: 200, data: {conversation}};
  }

  private async startUHConversation(
    params: StartUHConversationParams,
  ): Promise<StartConversationResponse> {
    if (this.conversation) {
      throw new Error('Conversation already started');
    }

    const {context, conversationId, channelConversationId, participant} =
      params;

    if (context?.placeholderConversation) {
      return this.startPlaceholderConversation(params);
    }

    if (shouldContextRequestPageContext(context)) {
      const result = await this.requestAction({
        name: 'request-action',
        waitForResponse: true,
        action: {
          name: 'get-page-context',
        },
      });

      if (result.data) {
        params.data = {
          ...params.data,
          ...result.data,
        };
      }
    }

    const uhResp = await this.startDisconnectedUHConversation({
      ...params,
      conversationId: channelConversationId,
    });

    if ('error' in uhResp) {
      return uhResp;
    }
    this.currentChannel = 'bot';

    const {conversation: uhConversation} = uhResp.data;

    const conversation = new Conversation(this, {
      id: conversationId || uhConversation.id,
      user: participant,
      status: uhConversation.status,
      syncStorage: this.syncStorageModeForChannel(
        'bot',
        this.uhClient.syncStorageMode,
      ),
      getSyncStorageMode: () => this.syncStorageMode,
      messages: {...uhConversation.messages},
      participants: {
        ...uhConversation.participants,
        [participant.id]: participant,
      },
      context: {
        ...context,
        ...uhConversation.context,
        channel: 'bot',
      },
    });
    conversation.setContextValue(
      this.uhConversationIdKey,
      channelConversationId || uhConversation.id,
    );
    this.conversation = conversation;

    this.listenForConversationEvents(conversation);

    // Don't need to fetch latest, since we get the latest as part of startConversation
    conversation.connect({fetch: false});

    // The initial v2 response can already hand off to a live channel, as in
    // embedded contact sales. Replay it after the bot listener is connected;
    // constructor context alone cannot notify the channel-change subscribers.
    // Externally managed conversations are activated by their view model later.
    const initialChannel = uhConversation.context.channel;
    if (
      this.conversation === conversation &&
      initialChannel &&
      initialChannel !== 'bot'
    ) {
      conversation.setContextValue('channel', initialChannel);
    }

    return {code: 200, data: {conversation}};
  }

  private async resumeChatConversation(
    params: ResumeChatConversationParams,
  ): Promise<StartConversationResponse> {
    // any existing conversation with this id is the stale pre-escalation
    // object (e.g. the bot conversation cached before another tab started the
    // chat); it is replaced by the conversation built here. A *different*
    // already-started conversation was rejected in resolveStartMode.
    const existingConversation = this.conversationForId(params.conversationId);

    const {conversationId, channelConversationId, participant, fetch} = params;

    const chatResp = await this.startDisconnectedChatConversation(
      {
        ...params,
        conversationId: channelConversationId,
        uhConversationId: conversationId,
      },
      {fetch},
    );

    if ('error' in chatResp) {
      return chatResp;
    }

    const {conversation: chatConversation} = chatResp.data;
    this.currentChannel = 'chat';

    const uhConversation = await this.fetchUHTranscriptForResume(
      conversationId,
      participant,
      params.context,
    );

    const providerArgs = await this.getProviderArgs();

    const conversation = this.assembleResumedConversation({
      channel: 'chat',
      id: conversationId,
      participant,
      status: chatConversation.status,
      defaultSyncStorageMode: this.chatClient.syncStorageMode,
      existingConversation,
      providerConversations: [uhConversation, chatConversation],
      baseContext: params.context,
      contextOverrides: {
        providerArgs,
        [this.uhConversationIdKey]: conversationId,
        [this.chatConversationIdKey]:
          channelConversationId || chatConversation.id,
      },
    });
    await this.disconnectStaleConversation(existingConversation);
    this.conversation = conversation;

    this.listenForConversationEvents(conversation);

    const transitionTimestamp = this.resumedTransitionTimestamp(uhConversation);
    if (transitionTimestamp !== undefined) {
      // pass the conversation explicitly: in threads, `this.conversation` is
      // backed by the view model, which has not adopted this conversation yet
      this.appendTransitionToAgentMetaEvent(transitionTimestamp, conversation);
    }

    if (conversation.isActive) {
      // The disconnected chat conversation and UH transcript were already
      // fetched above, so connecting the merged conversation must not fetch
      // either provider again.
      conversation.connect({fetch: false});
    }

    return {code: 200, data: {conversation}};
  }

  private async startRACConversation(
    params: StartRACConversationParams,
  ): Promise<StartConversationResponse> {
    const {conversationId, participant, context, status} = params;
    const existingConversation = conversationId
      ? this.conversationForId(conversationId)
      : this.conversation;
    this.currentChannel = 'request_a_call';

    const uhConversation = conversationId
      ? await this.fetchUHTranscriptForResume(
          conversationId,
          participant,
          context,
        )
      : null;

    // for RAC + email, we don't actually want to start a new
    // conversation. we just want to return the existing conversation.
    // at this point, the UH conversation would have ended. we
    // would just be displaying the conversation history.
    // the UI should route this conversation to the RAC screen
    const conversation = this.assembleResumedConversation({
      channel: 'request_a_call',
      id: conversationId || uuid(),
      participant,
      status,
      defaultSyncStorageMode: this.uhClient.syncStorageMode,
      existingConversation,
      providerConversations: [uhConversation],
      baseContext: context,
    });

    await this.disconnectStaleConversation(existingConversation);
    this.conversation = conversation;

    this.listenForConversationEvents(conversation);

    conversation.connect({fetch: false});

    return {code: 200, data: {conversation}};
  }

  // Builds the conversation shown when a live-support channel is resumed from
  // backend state, from the transcripts the caller has already fetched. Shared
  // by both live-support channels so chat and RAC can't drift in how they merge
  // and which context wins.
  //
  // Precedence, lowest to highest: the request context, then each provider
  // conversation in the order given, then this client's canonical channel
  // fields, then `contextOverrides`. `existingConversation` is the stale local
  // object for this id (e.g. the bot conversation cached before another tab
  // escalated): it contributes messages and participants, but never context —
  // its channel state is exactly what's being replaced.
  private assembleResumedConversation({
    channel,
    id,
    participant,
    status,
    defaultSyncStorageMode,
    existingConversation,
    providerConversations,
    baseContext,
    contextOverrides,
  }: {
    channel: ResumableLiveSupportChannel;
    id: string;
    participant: ConversationParticipant;
    status?: ConversationStatus;
    defaultSyncStorageMode: ConversationSyncStorageMode;
    existingConversation?: Conversation | null;
    // ordered lowest to highest precedence
    providerConversations: (Conversation | null | undefined)[];
    baseContext?: StartConversationParams['context'];
    contextOverrides?: ConversationContext;
  }): Conversation {
    // ids are namespaced per provider (chat ids are Twilio sids), so later
    // providers only overwrite an entry when it is genuinely the same message
    const sources = [existingConversation, ...providerConversations];

    const mergedMessages: Record<string, ConversationMessage> = {};
    const mergedParticipants: Record<string, ConversationParticipant> = {};
    sources.forEach((conversation) => {
      Object.assign(mergedMessages, conversation?.messages);
      Object.assign(mergedParticipants, conversation?.participants);
    });
    mergedParticipants[participant.id] = participant;

    const context: ConversationContext = {...baseContext};
    providerConversations.forEach((conversation) => {
      Object.assign(context, conversation?.context);
    });
    Object.assign(
      context,
      {
        channel,
        requestedChannel: channel,
        intent: 'contact_support',
        enabledInputModes: undefined,
      },
      contextOverrides,
    );

    const conversation = new Conversation(this, {
      id,
      user: participant,
      status,
      syncStorage: this.syncStorageModeForChannel(
        channel,
        defaultSyncStorageMode,
      ),
      getSyncStorageMode: () => this.syncStorageMode,
      messages: mergedMessages,
      participants: mergedParticipants,
      context,
    });

    // The Conversation constructor unconditionally sets
    // `<current channel>_conversationId` to the conversation's own id, which is
    // the backend id here, not the channel's. Re-assert the overrides
    // afterwards so the channel ids survive construction.
    if (contextOverrides) {
      conversation.setContextValues(contextOverrides);
    }

    return conversation;
  }

  private async disconnectStaleConversation(
    conversation: Conversation | null | undefined,
  ): Promise<void> {
    try {
      await conversation?.disconnect();
    } catch (e) {
      this.errors.reportWarning(
        `exception disconnecting stale conversation during resume: ${
          (e as Error).message
        }`,
        {tags: ERROR_TAGS},
      );
    }
  }

  private async getProviderArgs() {
    const response = await this.requestAction({
      name: 'request-action',
      waitForResponse: true,
      action: {
        name: 'get-provider-args',
      },
    });
    if (response.data) {
      return (response.data as Record<string, unknown>).providerArgs || {};
    }
    return {};
  }

  private async startDisconnectedUHConversation(
    params: StartConversationParams,
  ): Promise<StartConversationResponse> {
    return this.uhClient.startConversation({
      ...params,
      connect: false,
      syncStorage: false,
    });
  }

  private async startDisconnectedChatConversation(
    params: StartConversationParams,
    {fetch = false}: {fetch?: boolean} = {},
  ): Promise<StartConversationResponse> {
    return this.chatClient.startConversation({
      ...params,
      connect: false,
      fetch,
      syncStorage: false,
    });
  }

  // Fetches the UH (bot) transcript for a resumed live-support conversation so
  // the resumed thread can be seeded from the backend instead of localStorage.
  // Returns a disconnected conversation, or null when the transcript can't be
  // fetched — a failure must not fail the resume. Gated on
  // enableConversationsThreadsHistory (not enableConversationsThreads): the
  // latter still relies on localStorage for the pre-escalation transcript.
  private async fetchUHTranscriptForResume(
    conversationId: string,
    participant: ConversationParticipant,
    context?: StartConversationParams['context'],
  ): Promise<Conversation | null> {
    if (!this.featureFlags.enableConversationsThreadsHistory) {
      return null;
    }

    try {
      const resp = await this.uhClient.startConversation({
        conversationId,
        participant,
        context,
        connect: false,
        fetch: false,
        syncStorage: false,
        // the UH conversation has already been left by the time a live-support
        // channel starts; the v1 client otherwise gates inactive lookups on
        // enableConversationsThreads and the v2 client resumes directly by id
        includeInactive: true,
      });

      if ('error' in resp) {
        return null;
      }

      return resp.data.conversation;
    } catch (e) {
      this.errors.reportWarning(
        `exception fetching UH transcript for resume: ${(e as Error).message}`,
        {tags: ERROR_TAGS},
      );
      return null;
    }
  }

  // Timestamp to anchor the resumed "connected to an agent" marker at (not
  // whether an agent has actually joined): the server-side transition
  // timestamp when the transcript carries one, otherwise just after the last
  // bot message. Returns undefined when there is nothing to anchor to (no
  // marker is appended in that case — a marker stamped "now" would sort
  // after the chat history).
  private resumedTransitionTimestamp(
    uhConversation: Conversation | null,
  ): number | undefined {
    const contextTimestamp =
      uhConversation?.context?.channelTransitionTimestamp;
    if (typeof contextTimestamp === 'number') {
      return contextTimestamp;
    }

    const uhTimestamps = Object.values(uhConversation?.messages ?? {}).map(
      (message) => message.timestamp,
    );
    if (uhTimestamps.length === 0) {
      return undefined;
    }
    return Math.max(...uhTimestamps) + 1;
  }

  private listenForConversationEvents(conversation: Conversation) {
    this.conversationSub?.unsubscribe();

    const sub = new Subscription();
    this.conversationSub = sub;

    sub.add(
      this.uhClient.conversationEventsObservable().subscribe((event) => {
        if (
          this.conversation &&
          event.conversationId === this.uhConversationId &&
          // Once a live-support channel owns the conversation, deferred UH
          // events from leaving or fetching its transcript must not mutate
          // that conversation (or the stale bot object still displayed during
          // a warm attach).
          this.currentChannel !== 'chat' &&
          this.currentChannel !== 'request_a_call'
        ) {
          this.emitConversationEvent({
            ...event,
            conversationId: this.conversation.id,
          });
        }
      }),
    );

    sub.add(
      this.chatClient.conversationEventsObservable().subscribe((event) => {
        if (
          this.conversation &&
          event.conversationId === this.chatConversationId
        ) {
          this.emitConversationEvent({
            ...event,
            conversationId: this.conversation.id,
          });
        }
      }),
    );

    const events$ = conversation.eventsObservable();
    const context$ = conversation.contextObservable();

    sub.add(
      events$
        .pipe(filter((e) => e.name === 'conversation-request-action'))
        .subscribe((e) => {
          const event = e as ConversationRequestAction;
          const {action, respondWith, responseTimeout} = event;

          this.requestAction({
            name: 'request-action',
            action,
            responseTimeout,
            waitForResponse: !!respondWith,
          }).then((response) => {
            const data = response.data as Record<string, unknown> | undefined;
            respondWith?.(data);
          });
        }),
    );

    const conversationEnded$ = events$.pipe(
      filter((e) => e.name === 'conversation-ended'),
      take(1),
    );

    const restartedConversation$ = context$.pipe(
      distinctUntilChanged((prev, cur) => prev.intent === cur.intent),
      filter((context) => context.intent === 'restart_conversation'),
    );

    const channelChanged$ = context$.pipe(
      distinctUntilChanged((prev, cur) => prev.channel === cur.channel),
    );

    // In single-conversation mode, the channel can become chat before another
    // tab publishes the chat ID. Observe that later ID so this tab can attach.
    const chatIdChanged$ = this.enableCrossTabChatTransitionSync
      ? context$.pipe(
          map((context) => ({
            context,
            chatConversationId: context[this.chatConversationIdKey] as
              | string
              | undefined,
          })),
          distinctUntilChanged(
            (prev, cur) => prev.chatConversationId === cur.chatConversationId,
          ),
          // channelChanged$ handles the initial context emission. This stream
          // only needs to react to subsequent chat ID changes.
          skip(1),
          filter(
            ({context, chatConversationId}) =>
              context.channel === 'chat' && !!chatConversationId,
          ),
          map(({context}) => context),
        )
      : EMPTY;

    // Preserve channel-driven transitions and add late-ID transitions, while
    // deduplicating context updates that change both values at the same time.
    const transitionedToChat$ = merge(channelChanged$, chatIdChanged$).pipe(
      distinctUntilChanged(
        (prev, cur) =>
          prev.channel === cur.channel &&
          prev[this.chatConversationIdKey] === cur[this.chatConversationIdKey],
      ),
      filter((context) => context.channel === 'chat'),
    );

    const transitionedToRAC$ = channelChanged$.pipe(
      filter((context) => context.channel === 'request_a_call'),
    );

    const transitionedToEmail$ = channelChanged$.pipe(
      filter((context) => context.channel === 'email'),
    );

    // pass the conversation explicitly: in threads, `this.conversation` is
    // backed by the view model, which may not have adopted this conversation
    // yet (e.g. while resuming a live chat or RAC after a page reload)
    sub.add(
      transitionedToChat$.subscribe(() => {
        this.transitionToChat(conversation);
      }),
    );

    sub.add(
      transitionedToRAC$.subscribe(() => {
        this.transitionToRAC(conversation);
      }),
    );

    if (this.featureFlags.universalHelpUseInlineRacEmailForm) {
      sub.add(
        transitionedToEmail$.subscribe(() => {
          this.transitionToEmail(conversation);
        }),
      );
    }

    sub.add(
      restartedConversation$.subscribe(() => {
        this.restartConversation();
      }),
    );

    sub.add(
      conversationEnded$.subscribe(() => {
        if (!conversation.context.disableCleanupOnEnd) {
          conversation.disconnect();
        }
        this.appendChatEndedMetaEvent();

        // Don't clear conversation immediately if we're waiting for animations.
        // clearCurrentConversation will be called after animations complete and we show the chat ended message
        if (
          isDotComConversation(conversation) &&
          !conversation.context.supressChatEndedNotice
        ) {
          return;
        }
        this.clearCurrentConversation();
      }),
    );
  }

  async inProgress(params: InProgressParams): Promise<InProgressResponse> {
    const {conversationId, context} = params;

    let channelConversationId: string | undefined;
    if (context?.channel) {
      channelConversationId = this.conversationIdForChannel(
        params,
        context.channel,
      );
    }

    const channelParams = {
      ...params,
      // when we check individual channels, use the channel-specific id
      conversationId: channelConversationId || conversationId,
    };

    // when we're returning a response from this call, if we're returning
    // inProgress for a given conversation id, we should return that id
    const responseConversationId = conversationId || channelConversationId;

    try {
      if (context?.channel === 'request_a_call' || !context?.channel) {
        const racResp = await this.api.inProgressRequestACall(
          context?.entryPoint ?? '',
          context?.audience ?? '',
          this.uhConversationId,
        );

        if ('error' in racResp) {
          return racResp;
        }

        return {
          code: 200,
          data: {
            ...racResp.data,
            channel: 'request_a_call',
            conversationId: responseConversationId,
          },
        };
      }

      if (context?.channel === 'chat') {
        const chatResp = await this.chatClient.inProgress(channelParams);

        if ('error' in chatResp) {
          return chatResp;
        }

        return {
          code: 200,
          data: {
            ...chatResp.data,
            channel: 'chat',
            conversationId: responseConversationId,
          },
        };
      }

      const uhResp = await this.uhClient.inProgress({
        ...params,
        conversationId: this.conversationIdForChannel(params, 'bot'),
      });

      if ('error' in uhResp) {
        return uhResp;
      }

      const {conversationId: uhResponseConversationId} = uhResp.data;

      return {
        code: 200,
        data: {
          ...uhResp.data,
          channel: 'bot',
          conversationId: uhResponseConversationId ?? responseConversationId,
        },
      };
    } catch (e) {
      return this.error(500, (e as Error).message, ERROR_TAGS);
    }
  }

  async inProgressV2(params: InProgressParams): Promise<InProgressResponse> {
    const {conversationId, context} = params;

    let channelConversationId: string | undefined;
    if (context?.channel) {
      channelConversationId = this.conversationIdForChannel(
        params,
        context.channel,
      );
    }

    const responseConversationId = conversationId || channelConversationId;
    const chatParams = {
      ...params,
      conversationId: this.conversationIdForChannel(params, 'chat'),
    };
    const uhParams = {
      ...params,
      conversationId: this.conversationIdForChannel(params, 'bot'),
    };

    try {
      const respPromises: Promise<InProgressResponse>[] = [];
      if (context?.channel === 'chat') {
        respPromises.push(
          this.chatClient.inProgress(chatParams).then((chatResp) => {
            if ('error' in chatResp) {
              return chatResp;
            }

            return {
              code: 200,
              data: {
                ...chatResp.data,
                channel: 'chat',
                conversationId: responseConversationId,
              },
            };
          }),
        );
      }
      respPromises.push(
        this.api
          .inProgressRequestACall(
            context?.entryPoint ?? '',
            context?.audience ?? '',
            this.uhConversationId,
          )
          .then((racResp: InProgressResponse) => {
            if ('error' in racResp) {
              return racResp;
            }

            return {
              code: 200,
              data: {
                ...racResp.data,
                channel: 'request_a_call',
                conversationId: responseConversationId,
              },
            };
          }),
      );
      respPromises.push(
        this.uhClient.inProgress(uhParams).then((uhResp) => {
          if ('error' in uhResp) {
            return uhResp;
          }

          const {conversationId: uhResponseConversationId} = uhResp.data;

          return {
            code: 200,
            data: {
              ...uhResp.data,
              channel: 'bot',
              conversationId:
                uhResponseConversationId ?? responseConversationId,
            },
          };
        }),
      );

      const responses = await Promise.all(respPromises);

      return this.inProgressResponseForRequest(responses, params);
    } catch (e) {
      return this.error(500, (e as Error).message, ERROR_TAGS);
    }
  }

  private conversationIdForChannel(
    params: InProgressParams,
    channel: ConversationChannel,
  ): string | undefined {
    const {conversationId, context} = params;
    const channelConversationId = context?.[`${channel}_conversationId`] as
      | string
      | undefined;

    if (channelConversationId) {
      return channelConversationId;
    }

    if (channel === 'bot' || context?.channel === channel) {
      return conversationId;
    }

    return undefined;
  }

  private async transitionToChat(
    conversation: Conversation | null | undefined = this.conversation,
  ) {
    try {
      if (!conversation) {
        throw new Error('Conversation not started');
      }

      if (this.currentChannel === 'chat') {
        return;
      }
      const chatConversationId = conversation.context[
        this.chatConversationIdKey
      ] as string | undefined;
      const uhConversationId = conversation.context[
        this.uhConversationIdKey
      ] as string | undefined;

      // Storage-enabled clients share transition context between tabs, so only
      // the most-recent tab owns the full handoff and secondary tabs attach.
      // Storage-disabled Threads tabs do not receive each other's local
      // transition effects; they discover the backend channel on focus and
      // resume it through resumeChatConversation instead.
      if (
        this.enableCrossTabChatTransitionSync &&
        !conversation.isTabMostRecent()
      ) {
        await this.attachToExistingChat(conversation);
        return;
      }

      this.subscribeToClientStatus(this.chatClient);

      this.appendTransitionToAgentMetaEvent(undefined, conversation);

      await conversation.disconnect();
      if (this.syncStorageMode) {
        await conversation.persistToStorage();
      }

      const providerArgs = await this.getProviderArgs();

      // V2 transitions normally carry the backend-created Twilio id in the UH
      // context. The start-chat endpoint remains an idempotent fallback for V1
      // and failed proactive creation. Either way, this initiating transition
      // still owns its local cleanup and summary; channel existence alone does
      // not mean another frontend completed those steps.
      const resp = await this.startDisconnectedChatConversation({
        conversationId: chatConversationId,
        participant: conversation.user,
        context: {...conversation.context, providerArgs},
        uhConversationId,
      });

      if ('error' in resp) {
        this.incrementAndTrack(METRIC.transition_to_chat_error, {
          code: resp.code,
        });
        this.clearCurrentConversation();
        throw new Error(resp.error);
      }

      const {conversation: chatConversation} = resp.data;
      const uhCleanup = this.fetchLatestAndLeaveUHConversation(
        conversation,
        uhConversationId,
      ).catch((e) => {
        this.reportAndEmitException(
          `exception cleaning up UH conversation after starting chat: ${
            (e as Error).message
          }`,
          false,
        );
      });

      // set currentChannel after we've successfully started the chat conversation
      this.currentChannel = 'chat';

      conversation.setContextValues({
        ...chatConversation.context,
        [this.chatConversationIdKey]: chatConversation.id,
        channel: 'chat',
        requestedChannel: 'chat',
        intent: 'contact_support',
      });

      await conversation.connect({fetch: true});
      await uhCleanup;

      conversation.setContextValue('enabledInputModes', undefined);

      if (this.syncStorageMode) {
        await conversation.persistToStorage();
      }

      // these test + synthetic messages use the chat client directly, since otherwise
      // they'd get swallowed and not sent since we intercept them in this client
      if (conversation.context.dev) {
        devLog('Initiating internal test chat');
        await this.chatClient.sendConversationMessage({
          conversationId: chatConversation.id,
          message: {
            ...conversation.buildTextMessage('Stripe Internal Test'),
            nonce: 'ignore-internal-test-message',
          },
        });
      } else if (conversation.context.syntheticEndChat) {
        devLog('Initiating internal synthetic end chat test chat');
        await this.chatClient.sendConversationMessage({
          conversationId: chatConversation.id,
          message: {
            ...conversation.buildTextMessage(
              'Stripe Internal Synthetic Test - End Chat',
            ),
            nonce: 'ignore-internal-synthetic-end-chat-test-message',
          },
        });
      } else if (conversation.context.synthetic) {
        devLog('Initiating internal synthetic test chat');
        await this.chatClient.sendConversationMessage({
          conversationId: chatConversation.id,
          message: {
            ...conversation.buildTextMessage('Stripe Internal Synthetic Test'),
            nonce: 'ignore-internal-synthetic-test-message',
          },
        });
      }

      const isSyntheticTransition =
        conversation.context.synthetic || conversation.context.syntheticEndChat;
      if (!isSyntheticTransition) {
        await conversation.sendMessage({
          ...this.buildUHConversationSummaryMessage(conversation),
          nonce: 'ignore-initial-uh-chat-context-message',
        });
      }

      this.incrementAndTrack(METRIC.transition_to_chat_success);
    } catch (e) {
      this.reportAndEmitException(
        `exception transitioning conversation to chat: ${(e as Error).message}`,
        false,
      ); // Not a 5xx error
      this.clearCurrentConversation();
      this.incrementAndTrack(METRIC.transition_to_chat_error);
    }
  }

  // If we've already transitioned to chat in another tab, we'll call
  // this path, which skips some steps of the full transition logic and
  // just connects to the existing chat conversation.
  private async attachToExistingChat(
    conversation: Conversation | null | undefined = this.conversation,
  ) {
    try {
      const chatConversationId = conversation?.context[
        this.chatConversationIdKey
      ] as string | undefined;
      if (!conversation || !chatConversationId) {
        return;
      }

      this.subscribeToClientStatus(this.chatClient);

      await conversation.disconnect();

      const resp = await this.startDisconnectedChatConversation({
        conversationId: chatConversationId,
        participant: conversation.user,
        context: {...conversation.context},
        uhConversationId: conversation.context[this.uhConversationIdKey] as
          | string
          | undefined,
      });

      if ('error' in resp) {
        this.clearCurrentConversation();
        throw new Error(resp.error);
      }

      // set currentChannel after we've successfully connected to existing chat conversation
      this.currentChannel = 'chat';

      await conversation.connect({fetch: true});
    } catch (e) {
      this.clearCurrentConversation();
      this.reportAndEmitException(
        `exception adopting chat from other tab: ${(e as Error).message}`,
        false,
      );
    }
  }

  private appendTransitionToAgentMetaEvent(
    timestamp?: number,
    conversation: Conversation | null | undefined = this.conversation,
  ) {
    if (!conversation) {
      return;
    }

    if (
      this.featureFlags.universalHelpUseNewDesign &&
      this.featureFlags.enableUniversalHelp
    ) {
      conversation.log.appendMetaEvent({
        id: 'transition-to-agent',
        message: messages.transitionToAgentV2,
        style: 'caption',
        timestamp,
      });
    } else if (isDotComConversation(conversation)) {
      conversation.log.appendMetaEvent({
        id: 'transition-to-agent',
        message: messages.transitionToAgentDotCom,
        timestamp,
      });
    } else {
      conversation.log.appendMetaEvent({
        id: 'transition-to-agent',
        message: messages.transitionToAgent,
        timestamp,
      });
    }
  }

  private appendChatEndedMetaEvent() {
    if (!this.conversation) {
      return;
    }

    if (this.conversation.context.supressChatEndedNotice) {
      return;
    }

    if (
      this.featureFlags.universalHelpUseNewDesign &&
      this.featureFlags.enableUniversalHelp
    ) {
      return;
    }

    if (isDotComConversation(this.conversation)) {
      this.setContextToWaitForAnimations();
      return;
    }

    this.conversation.log.appendMetaEvent({
      id: 'chat-ended-notice',
      message: messages.chatEndedMessage,
    });
  }

  private setContextToWaitForAnimations() {
    if (!this.conversation) {
      return;
    }

    const currentLogItems = this.conversation.log.get();

    // Set a flag to indicate that we're waiting for animations to complete and
    // store the target items length - this is the length we need to reach for animations to be complete.
    // This prevents the target from moving if new items are added after the conversation ends
    this.conversation.setContextValues({
      waitingForAnimationsToComplete: true,
      animationTargetLength: currentLogItems.length,
    });

    // The animation system will signal completion via the animations-complete action
  }

  private showChatEndedMessage() {
    if (!this.conversation) {
      return;
    }

    this.conversation.clearContextValues([
      'waitingForAnimationsToComplete',
      'animationTargetLength',
    ]);

    this.conversation.log.appendMetaEvent({
      id: 'chat-ended-notice',
      message: messages.chatEndedMessage,
    });
  }

  private async transitionToRAC(
    conversation: Conversation | null | undefined = this.conversation,
  ) {
    try {
      if (!conversation) {
        throw new Error('Conversation not started');
      }

      if (this.currentChannel === 'request_a_call') {
        return;
      }
      this.currentChannel = 'request_a_call';

      await conversation.disconnect();
      if (this.syncStorageMode) {
        await conversation.persistToStorage();
      }

      await this.fetchLatestAndLeaveUHConversation(
        conversation,
        conversation.context[this.uhConversationIdKey] as string | undefined,
      );

      // we just ended the UH conversation. we want to re-subscribe,
      // but not re-fetch since there's no conversation to fetch
      await conversation.connect({fetch: false});

      conversation.setContextValues({
        channel: 'request_a_call',
        requestedChannel: 'request_a_call',
        intent: 'contact_support',
        enabledInputModes: [],
      });

      if (this.syncStorageMode) {
        await conversation.persistToStorage();
      }

      this.incrementAndTrack(METRIC.transition_to_rac_success);
    } catch (e) {
      this.reportAndEmitException(
        `exception transitioning conversation to rac: ${(e as Error).message}`,
        false,
      ); // Not a 5xx error
      this.incrementAndTrack(METRIC.transition_to_rac_error);
    }
  }

  private async transitionToEmail(
    conversation: Conversation | null | undefined = this.conversation,
  ) {
    try {
      if (!conversation) {
        throw new Error('Conversation not started');
      }

      if (this.currentChannel === 'email') {
        return;
      }
      this.currentChannel = 'email';

      conversation.setContextValue('supressChatEndedNotice', true);
      conversation.leave();

      this.incrementAndTrack(METRIC.transition_to_email_success);
    } catch (e) {
      this.reportAndEmitException(
        `exception transitioning conversation to email: ${
          (e as Error).message
        }`,
        false,
      ); // Not a 5xx error
      this.incrementAndTrack(METRIC.transition_to_email_error);
    }
  }

  private async fetchLatestAndLeaveUHConversation(
    conversation = this.conversation,
    uhConversationId = this.uhConversationId,
  ) {
    if (!conversation || !uhConversationId) {
      return;
    }
    conversation.clearTypers();

    // Threads-only: the backend ends the assistant conversation itself once a
    // transition to chat or RAC completes, so there's nothing left to leave
    // locally here. Calling leaveConversation anyway would route through the
    // all-in-one end endpoint, which for chat also runs EndLiveChat and closes
    // the channel the server just created.
    const leaveConversation = !this.serverEndsUhConversationOnTransition;

    const uhConversationEnded = leaveConversation
      ? firstValueFrom(
          this.uhClient
            .conversationEventsObservable()
            .pipe(
              filter(
                (e) =>
                  e.name === 'conversation-updated' &&
                  e.conversationId === uhConversationId &&
                  e.status === ConversationStatus.Inactive,
              ),
            ),
        )
      : undefined;

    // we make sure we fetch the latest messages before we leave + disconnect
    const latestUHMessagesResp = await this.uhClient.getConversationMessages({
      conversationId: uhConversationId,
    });

    if (leaveConversation) {
      await this.uhClient.leaveConversation({
        conversationId: uhConversationId,
        participant: UniversalHelpUser,
      });
    }

    // we emit all the latest UH messages here, since they're emitted async
    // and might get emitted while the conversation is disconnect and leaving
    // the UH conversation
    if (!('error' in latestUHMessagesResp)) {
      const {messages: latestUHMessages} = latestUHMessagesResp.data;
      Object.values(latestUHMessages).forEach((message) => {
        conversation.handleEventFromClient(
          this.newConversationEvent({
            name: 'conversation-message-added',
            conversationId: conversation.id,
            participantId: message.participant.id,
            messageId: message.id,
            message,
          }),
        );
      });
    }

    if (uhConversationEnded) {
      await uhConversationEnded;
    }
  }

  private async restartConversation() {
    try {
      if (!this.conversation) {
        throw new Error('Conversation not started');
      }

      if (this.currentChannel !== 'bot') {
        throw new Error('Can only restart bot conversations');
      }

      this.conversation.clearTypers();

      await this.conversation.disconnect();
      await this.uhClient.leaveConversation({
        conversationId: this.conversation.id,
        participant: UniversalHelpUser,
      });

      const resp = await this.startDisconnectedUHConversation({
        participant: this.conversation.user,
        context: {...this.conversation.context},
      });

      if ('error' in resp) {
        throw new Error(resp.error);
      }

      this.conversation.setContextValues({
        ...resp.data.conversation.context,
      });

      await this.conversation.connect({fetch: true});
    } catch (e) {
      this.reportAndEmitException(
        `exception restarting conversation: ${(e as Error).message}`,
        false,
      ); // Not a 5xx error
    }
  }

  get chatConversationIdKey(): string {
    return `${this.chatClient.channel}_conversationId`;
  }

  get uhConversationIdKey(): string {
    return `${this.uhClient.channel}_conversationId`;
  }

  get chatConversationId(): string | undefined {
    const currentConversation = this.conversation;
    const conversation =
      currentConversation?.context.channel === 'chat'
        ? currentConversation
        : undefined;
    if (conversation?.context) {
      return conversation.context[this.chatConversationIdKey] as
        | string
        | undefined;
    }
    if (
      this.localConversation &&
      this.localConversation.id === currentConversation?.id
    ) {
      return this.localConversation.context[this.chatConversationIdKey] as
        | string
        | undefined;
    }
  }

  get uhConversationId(): string | undefined {
    if (this.conversation?.context) {
      return this.conversation?.context[this.uhConversationIdKey] as
        | string
        | undefined;
    }
  }

  private conversationForId(
    conversationId: string,
  ): Conversation | null | undefined {
    if (this.localConversation?.id === conversationId) {
      return this.localConversation;
    }
    const currentConversation = this.conversation;
    if (currentConversation?.id === conversationId) {
      return currentConversation;
    }
  }

  get type(): string {
    return UHLiveChannelsClientType;
  }

  get channel(): ConversationChannel {
    return this.currentChannel;
  }

  get syncStorageMode(): ConversationSyncStorageMode {
    if (this.currentChannel === 'chat') {
      return this.syncStorageModeForChannel(
        'chat',
        this.chatClient.syncStorageMode,
      );
    }
    return this.syncStorageModeForChannel(
      this.currentChannel,
      this.uhClient.syncStorageMode,
    );
  }

  private syncStorageModeForChannel(
    channel: ConversationChannel,
    defaultSyncStorageMode: ConversationSyncStorageMode,
  ): ConversationSyncStorageMode {
    return this.getSyncStorageModeForChannel(channel, defaultSyncStorageMode);
  }

  get analyticsParams(): Record<string, unknown> {
    const universalHelpConversationId = this.uhConversationId;
    const audience = this.conversation?.context.audience;
    const userId = this.conversation?.user.id;
    const triggerPoint = this.conversation?.context.triggerParams?.triggerPoint;
    return {
      universalHelpConversationId,
      audience,
      userId,
      triggerPoint,
    };
  }

  disconnect = async (): Promise<void> => {
    if (this.currentChannel === 'chat') {
      return this.chatClient.disconnect();
    }
    return this.uhClient.disconnect();
  };

  dispose(): void {
    this.lifecycleSub.unsubscribe();
    this.clientStatusSub?.unsubscribe();
    this.clientStatusSub = null;
    this.conversationSub?.unsubscribe();
    this.conversationSub = null;

    // The channel clients are injected transports with caller-owned lifetimes.
    // Release only subscriptions owned by this composite client.
    super.dispose();
  }

  action = async (
    params: ConversationActionParams,
  ): Promise<ClientResponse> => {
    try {
      if (!window.navigator.onLine) {
        return this.error(408, 'No network connection');
      }

      if (this.currentChannel === 'chat') {
        // TODO(joesteele): if we're in a live chat and a trigger comes in
        //  handle it, by showing a blocking confirmation modal or something
        //  as we don't want it to blow away their live chat with the agent.
        //  currently, this will just ignore the action, doing nothing.
        //  without this guard though, the chat client would not be able to
        //  handle the action and it would throw an error, aborting the
        //  in-progress chat conversation.
        if (params.action.name === 'handle-trigger-params') {
          return {code: 205};
        }

        // avoiding flooding the dashboard error logs with errors like this
        // "[stripe_assistant]: [twilio-conversations-client]: action not supported"
        if (params.action.name === 'inline-data-result') {
          return {code: 205};
        }

        return this.chatClient.action({
          ...params,
          conversationId: this.chatConversationId as string,
        });
      }

      const inlineRacAndEmail =
        this.featureFlags.universalHelpUseNewDesign &&
        this.featureFlags.universalHelpUseInlineRacEmailForm;

      const {action, token} = params;

      if (action.name === 'get-smart-suggestions') {
        if (!!this.conversation && !this.conversation.isTabMostRecent()) {
          return {code: 205};
        }

        if (this.conversation?.context.placeholderConversation) {
          return {code: 205};
        }

        if (this.uhClient.isPending() || this.isInlineDataActive()) {
          return {code: 205};
        }

        const latestItem = this.getLatestLogItem();
        let message;
        if (latestItem?.name === 'conversation-message-added') {
          message = latestItem.message as ConversationMessage;
        }
        if (message?.type === 'button-options-conversation-message') {
          // if the latest message was a prompt, replace it by
          // passing the `get-smart-suggestions` as the selected option
          const replaceSuggestionsAction: ConversationAction = {
            name: 'select-option',
            message,
            selectedOptionId: action.name,
            data: action.data,
          };

          return this.action({
            token,
            action: replaceSuggestionsAction,
            conversationId: this.uhConversationId as string,
          });
        }
      }

      const clean = (selected: string) => {
        return selected.replace(/^topic_/, '').replace(/^channel_/, '');
      };

      if (action.name === 'select-option') {
        const {message, selectedOptionId} = action as SelectOptionAction;

        if (message.purpose === 'channel_selection') {
          this.analytics.track('channel_selected', {
            channel: clean(selectedOptionId),
          });
          // intercept RAC + email, setting the intent and requestedChannel if we're not using
          // the inline forms within the Inline Data
          if (selectedOptionId === 'channel_rac') {
            if (inlineRacAndEmail) {
              // so we can trigger the change in scroll behavior
              this.conversation?.setContextValues({
                intent: 'contact_support',
              });
            } else {
              this.conversation?.setContextValues({
                requestedChannel: 'request_a_call',
                intent: 'contact_support',
              });
            }

            // hack: this 205 code is used to signal to the UI
            // that the request was accepted, but not to treat
            // it as the value being set.
            // this was a workaround for intercepting these
            // UI actions to display the modals instead of
            // sending the requests to the server.
            return {code: 205};
          } else if (selectedOptionId === 'channel_email') {
            if (inlineRacAndEmail) {
              // so we can trigger the change in scroll behavior
              this.conversation?.setContextValues({
                intent: 'contact_support',
              });
            } else {
              this.conversation?.setContextValues({
                requestedChannel: 'email',
                intent: 'contact_support',
              });
            }
            // hack: this 205 code is used to signal to the UI
            // that the request was accepted, but not to treat
            // it as the value being set.
            // this was a workaround for intercepting these
            // UI actions to display the modals instead of
            // sending the requests to the server.
            return {code: 205};
          } else if (selectedOptionId === 'channel_chat') {
            this.conversation?.setContextValues({
              requestedChannel: 'chat',
              intent: 'contact_support',
            });
          } else if (selectedOptionId === 'channel_restart') {
            this.conversation?.clearContextValues([
              'intent',
              'requestedChannel',
            ]);
          }
        }

        if (message.purpose === 'topic_selection') {
          this.analytics.track('topic_selected', {
            topic: clean(selectedOptionId),
          });
          this.conversation?.setContextValue(
            'topic',
            selectedOptionId.replace(/^topic_/, ''),
          );
        }

        // Emit an extra analytics event if the user clicked a suggested question
        // with the selected question message for simpler experimentation analysis
        if (selectedOptionId.includes('question_')) {
          const selectedOption = message.options.find(
            (option) => option.id === selectedOptionId,
          );
          this.analytics.track('suggested_question_selected', {
            question: selectedOption?.label,
            optionId: selectedOptionId,
          });
        }
      }

      if (action.name === 'article-visited') {
        const {data} = action;
        const article = data as ConversationArticle;

        let uhContext = this.conversation?.context.universalHelp as
          | UniversalHelpConversationContext
          | undefined;
        if (!uhContext) {
          uhContext = {};
        }
        const articlesVisited =
          (uhContext.articlesVisited as Record<string, ConversationArticle>) ||
          {};
        articlesVisited[article.id] = article;
        uhContext.articlesVisited = articlesVisited;
        this.conversation?.setContextValue('universalHelp', uhContext);
      }

      if (action.name === 'inline-data-result' && !!this.conversation) {
        if (!this.conversation.isTabMostRecent()) {
          this.analytics.track('inactive_tab_inline_data_action_ignored');
          return {code: 205};
        }
      }

      if (action.name === 'inline-data-action' && !!this.conversation) {
        const {data} = action;

        const dataInline = data as Record<string, unknown>;

        if (dataInline && dataInline.purpose === 'channel_selection') {
          const channelID = dataInline.channelID as string;
          this.analytics.track('channel_selected', {
            channel: channelID,
          });
          if (channelID === 'channel_rac') {
            if (inlineRacAndEmail) {
              // so we can trigger the change in scroll behavior
              this.conversation?.setContextValues({
                intent: 'contact_support',
              });
            } else {
              this.conversation?.setContextValues({
                requestedChannel: 'request_a_call',
                intent: 'contact_support',
              });
            }

            return {code: 205};
          } else if (channelID === 'channel_email') {
            if (inlineRacAndEmail) {
              // so we can trigger the change in scroll behavior
              this.conversation?.setContextValues({
                intent: 'contact_support',
              });
            } else {
              this.conversation?.setContextValues({
                requestedChannel: 'email',
                intent: 'contact_support',
              });
            }

            return {code: 205};
          }
        }
      }

      // handle animations complete event
      if (action.name === 'animations-complete') {
        if (
          this.conversation &&
          this.conversation.context.waitingForAnimationsToComplete
        ) {
          this.showChatEndedMessage();
          this.clearCurrentConversation();
        }
        return {code: 200};
      }

      // if something triggered a request to open UH, handle that trigger
      if (action.name === 'handle-trigger-params' && !!this.conversation) {
        // in this case, if we have an ongoing conversation, and the
        // trigger contained a query, send that query as a user message
        const triggerParams = action.triggerParams as TriggerParams;
        const {initialQuery, initialAttachment} = triggerParams;
        if (initialQuery || initialAttachment) {
          // If an interrupt is *already* being handled, update the message and wait for it to be handled
          if (this.nextTriggerQuery) {
            this.nextTriggerQuery = initialQuery;
            return {code: 205};
          }
          this.nextTriggerQuery = initialQuery;

          // Allow in-flight requests to finish before handling interrupts
          await this.uhClient.waitForPendingMessagesHandled();

          // If there's an ongoing action send an interrupt and wait for the inline data to handle it
          if (this.isInterruptableInlineDataActive()) {
            this.nextTriggerQuery = initialQuery;

            const event = {name: 'inline-data-interruption', nonce: uuid()};
            const result = await this.inlineDataEventBus.emitWithResult(event, {
              timeoutMs: 5_000,
            });

            if (result.error === 'timeout') {
              this.errors.reportWarning(
                'Timed out attempting to interrupt inline data',
                {tags: ERROR_TAGS},
              );
            }
          }

          if (this.nextTriggerQuery || initialAttachment) {
            const resp = await this.conversation.sendMessage(
              initialAttachment
                ? this.conversation.buildTextWithAttachmentsMessage(
                    this.nextTriggerQuery ?? '',
                    [
                      {
                        name: initialAttachment.name,
                        size: initialAttachment.size,
                        type: initialAttachment.type,
                        upload_token: initialAttachment.upload_token,
                      },
                    ],
                  )
                : this.conversation.buildTextMessage(
                    this.nextTriggerQuery ?? '',
                  ),
            );
            this.nextTriggerQuery = undefined;
            return resp;
          } else {
            return {code: 205};
          }
        }
      }

      if (
        this.conversation &&
        shouldConversationSendPageContext(this.conversation)
      ) {
        const result = await this.requestAction({
          name: 'request-action',
          waitForResponse: true,
          action: {
            name: 'get-page-context',
          },
        });

        if (result.data) {
          action.data = {
            ...(action.data ?? {}),
            ...result.data,
          };
        }
      }

      const prevInputModes = this.conversation?.context.enabledInputModes;

      const resp = await this.uhClient.action({
        token,
        action,
        conversationId: this.uhConversationId as string,
      });

      if (
        resp.code >= 400 &&
        !!this.conversation &&
        !!prevInputModes &&
        !isEqual(this.conversation.context.enabledInputModes, prevInputModes)
      ) {
        // if the message failed, restore the previous input modes
        this.conversation.setContextValue('enabledInputModes', prevInputModes);

        if (
          action.name === 'select-option' &&
          action.selectedOptionId === 'channel_chat'
        ) {
          // if the request failed, reset contact support intent
          this.conversation.clearContextValues(['intent', 'requestedChannel']);
        }
      }

      // this needs to happen after we reset the previous input modes,
      // otherwise it might think it's stuck when it's not
      this.checkForStuckConversation(resp, this.conversation);

      return resp;
    } catch (e) {
      const clientErrorResponse = await this.reportAndEmitException(
        `exception sending conversation action: ${(e as Error).message}`,
      );
      return clientErrorResponse;
    }
  };

  private getLatestLogItem(): ConversationLogItem | null {
    const log = this.conversation?.log.get();

    if (!log || log.length === 0) {
      return null;
    }

    return log[log.length - 1];
  }

  private isActiveInlineDataLogItem(
    logItem: ConversationLogItem | null,
  ): logItem is InlineDataLogItem {
    return (
      !!logItem &&
      logItem.name === 'conversation-message-added' &&
      logItem.message.type === 'inline-data-conversation-message' &&
      !logItem.message.inlineData.isDone
    );
  }

  private isInlineDataActive(): boolean {
    const logItem = this.getLatestLogItem();
    return this.isActiveInlineDataLogItem(logItem);
  }

  private isInterruptableInlineDataActive(): boolean {
    const logItem = this.getLatestLogItem();
    if (!this.isActiveInlineDataLogItem(logItem)) {
      return false;
    }

    const {inlineData} = logItem.message;
    return INTERRUPTABLE_INLINE_DATA_NAMES.includes(inlineData.name);
  }

  releaseConversation(conversation: Conversation) {
    if (this.conversation === conversation) {
      this.clearCurrentConversation();
    }
  }

  private clearCurrentConversation() {
    this.conversationSub?.unsubscribe();
    this.conversationSub = null;
    this.conversation = null;
    this.currentChannel = 'bot';
    this.subscribeToClientStatus(this.uhClient);
  }

  private isRetryableEndLiveChatResponse(response: ClientResponse): boolean {
    return (
      response.code == null ||
      response.code === 408 ||
      response.code === 429 ||
      (response.code >= 500 && response.code <= 599)
    );
  }

  private async endChatConversationOnBackend(
    params: LeaveConversationParams,
    attempt = 1,
  ): Promise<ClientResponse> {
    try {
      const response = await this.uhClient.leaveConversation(params);
      if (
        !this.isRetryableEndLiveChatResponse(response) ||
        attempt === MAX_END_LIVE_CHAT_ATTEMPTS
      ) {
        return response;
      }
    } catch (error) {
      if (attempt === MAX_END_LIVE_CHAT_ATTEMPTS) {
        throw error;
      }
    }

    await new Promise<void>((resolve) => {
      setTimeout(resolve, END_LIVE_CHAT_RETRY_DELAY_MS * attempt);
    });
    return this.endChatConversationOnBackend(params, attempt + 1);
  }

  leaveConversation = async (
    params: LeaveConversationParams,
  ): Promise<ClientResponse> => {
    try {
      if (this.currentChannel === 'chat') {
        if (this.conversation) {
          const agentPresent = Object.values(
            this.conversation.participants,
          ).some((p) => p.role === 'agent');

          // If abandoned chat, count messages and cancel task
          if (!agentPresent) {
            this.api
              .abandonChat({
                conversationId: this.chatConversationId as string,
              })
              .then(() => {
                this.incrementAndTrack(METRIC.abandon_chat_success);
              })
              .catch((e: Error) => {
                this.reportAndEmitException(
                  `exception handling abandoned chat: ${e.message}`,
                  false,
                ); // Not a 5xx error

                this.incrementAndTrack(METRIC.abandon_chat_error);
              });
          }
        }
        const resp = await this.chatClient.leaveConversation({
          ...params,
          conversationId: this.chatConversationId as string,
        });

        // Ends live chat/RAC/the conversation itself on the backend in one
        // call, so that conversation.chat_status updates synchronously
        // instead of waiting on the async support hook. Attempted
        // regardless of the Twilio-side result above (EndLiveChat is
        // idempotent), and its result takes precedence since it's the
        // authoritative signal that the conversation actually ended.
        const backendResp = this.endChatConversationOnLeave
          ? await this.endChatConversationOnBackend({
              ...params,
              conversationId: this.uhConversationId as string,
            })
          : undefined;

        setTimeout(() => {
          this.clearCurrentConversation();
        }, 250);

        return backendResp ?? resp;
      }

      const {conversationId} = params;

      const resp = await this.uhClient.leaveConversation({
        ...params,
        conversationId: conversationId ?? (this.uhConversationId as string),
      });

      setTimeout(() => {
        this.clearCurrentConversation();
      }, 250);

      return resp;
    } catch (e) {
      const clientErrorResponse = await this.reportAndEmitException(
        `exception leaving conversation: ${(e as Error).message}`,
      );
      return clientErrorResponse;
    }
  };

  getConversationMessages = async (
    params: GetConversationMessagesParams,
  ): Promise<GetConversationMessagesResponse> => {
    if (this.currentChannel === 'chat') {
      const conversation = this.conversationForId(params.conversationId);
      // Message fetches merge additively into the conversation, so the UH
      // transcript seeded when the chat was resumed survives them; only the
      // chat provider needs to be consulted here.
      return this.chatClient.getConversationMessages({
        ...params,
        conversationId: conversation?.context[
          this.chatConversationIdKey
        ] as string,
      });
    }
    return this.uhClient.getConversationMessages({
      ...params,
      conversationId: this.uhConversationId as string,
    });
  };

  pollConversation = async (params: {
    conversationId: string;
    participantsById: Record<string, ConversationParticipant>;
    user?: ConversationParticipant;
    dynamicMessageIds?: string[];
  }) => {
    const {uhClient} = this;
    if (
      !('pollConversation' in uhClient) ||
      typeof uhClient.pollConversation !== 'function'
    ) {
      return {type: 'success' as const, messages: {}};
    }

    return uhClient.pollConversation({
      ...params,
      conversationId: this.uhConversationId ?? params.conversationId,
    });
  };

  getConversationParticipants = async (
    params: GetConversationParticipantsParams,
  ): Promise<GetConversationParticipantsResponse> => {
    if (this.currentChannel === 'chat') {
      const conversation = this.conversationForId(params.conversationId);
      return this.chatClient.getConversationParticipants({
        ...params,
        conversationId: conversation?.context[
          this.chatConversationIdKey
        ] as string,
      });
    }
    return this.uhClient.getConversationParticipants();
  };

  sendConversationMessage = async (
    params: SendConversationMessageParams,
  ): Promise<ClientResponse> => {
    try {
      const {conversationId, message} = params;
      if (
        (message.type === 'text-conversation-message' ||
          message.type === 'text-with-attachments-conversation-message') &&
        !this.conversation
      ) {
        // if we don't have a conversation,  we're triggering a new conversation
        // from the screen of an ended conversation. emit an event to reload
        const firstAttachment =
          message.type === 'text-with-attachments-conversation-message'
            ? message.attachments[0]
            : undefined;

        this.emitNewConversationEvent({
          name: 'conversation-set-context-values',
          conversationId,
          values: {
            restartConversation: {
              initialQuery: message.text,
              ...(firstAttachment?.upload_token
                ? {
                    initialAttachment: {
                      name: firstAttachment.name,
                      size: firstAttachment.size,
                      type: firstAttachment.type,
                      upload_token: firstAttachment.upload_token,
                    },
                  }
                : {}),
            },
          },
        });
        return {code: 205};
      }

      if (
        (message.type === 'text-with-attachments-conversation-message' ||
          message.type === 'text-conversation-message') &&
        !!this.conversation
      ) {
        // Always set the 'latestQuestion' on first message. If it's been identified as a
        // dev, synthetics, or synthetic end-chat mode, the 'latestQuestion' will remain as the triggering
        // 'Stripe Internal (Synthetic) Test' message.
        // Otherwise, keep updating the 'latestQuestion` with the newest user message.
        // 'latestQuestion' is then used to prefill email/rac forms, or sent along in chat as context.
        if (
          !this.conversation.context.dev &&
          !this.conversation.context.synthetic &&
          !this.conversation.context.syntheticEndChat
        ) {
          this.conversation.setContextValue('latestQuestion', message.text);
        }

        let swallowMessage = false;
        if (message.text.match(INTERNAL_TEST_STRING)) {
          this.conversation.setContextValues({
            dev: true,
          });
          swallowMessage = true;
        } else if (message.text.match(INTERNAL_SYNTHETIC_TEST_STRING)) {
          this.conversation.setContextValues({
            synthetic: true,
          });
          swallowMessage = true;
        } else if (
          message.text.match(INTERNAL_SYNTHETIC_END_CHAT_TEST_STRING)
        ) {
          this.conversation.setContextValues({
            synthetic: true,
            syntheticEndChat: true,
          });
          swallowMessage = true;
        }

        if (swallowMessage) {
          // we'll swallow the message by updating message
          // with a new id marking it to be ignored
          const {id} = message;
          const testId = 'ignore-internal-test-message';

          this.emitNewConversationEvent({
            name: 'conversation-message-updated',
            conversationId,
            participantId: message.participant.id,
            messageId: testId,
            nonce: message.nonce,
            message: {
              ...message,
              id: testId,
            },
          });

          // since the conversation has already added a pending message,
          // we'll also remove that message from the log by deferring the operation
          setTimeout(() => {
            if (this.conversation) {
              const log = this.conversation?.log.get();
              this.conversation?.log.set(log.filter((m) => m.id !== id));
            }
          }, 1);

          return {code: 200};
        }
      }

      if (this.currentChannel === 'chat') {
        return this.chatClient.sendConversationMessage({
          ...params,
          conversationId: this.chatConversationId as string,
        });
      }

      if (
        this.conversation &&
        shouldConversationSendPageContext(this.conversation)
      ) {
        const result = await this.requestAction({
          name: 'request-action',
          waitForResponse: true,
          action: {
            name: 'get-page-context',
          },
        });

        if (result.data) {
          message.data = {
            ...message.data,
            ...result.data,
          };
        }
      }

      let prevPromptMessage: ButtonOptionsConversationMessage | undefined;
      if (
        message.type === 'text-conversation-message' ||
        message.type === 'text-with-attachments-conversation-message'
      ) {
        const log = this.conversation?.log.get();

        const lastItem = log && log.length > 0 && log[log.length - 1];
        let lastMessage: ConversationMessage | undefined;

        if (lastItem && lastItem.name === 'conversation-message-added') {
          lastMessage = lastItem.message;
        }
        // depending on the timing, the last message could be this message.
        // I believe this may happen due to the conversation optimistically
        // adding the message in a 'pending' state. when that happens, we
        // look for the previous message before it.
        if (lastMessage?.id === message.id) {
          const prevItem = log && log.length > 1 && log[log.length - 2];
          if (prevItem && prevItem.name === 'conversation-message-added') {
            lastMessage = prevItem.message;
          }
        }

        if (lastMessage?.type === 'button-options-conversation-message') {
          const optionId =
            message.type === 'text-with-attachments-conversation-message'
              ? 'text-with-attachments-input'
              : 'text-input';
          this.emitNewConversationEvent({
            name: 'conversation-message-updated',
            conversationId: this.conversation?.id as string,
            participantId: lastMessage.participant.id,
            messageId: lastMessage.id,
            message: {
              ...prevPromptMessage,
              ...lastMessage,
              selectedOptionId: optionId,
            },
          });
        }
      }

      const prevInputModes = this.conversation?.context.enabledInputModes;

      const resp = await this.uhClient.sendConversationMessage({
        ...params,
        conversationId: this.uhConversationId as string,
      });

      if (
        resp.code >= 400 &&
        !!this.conversation &&
        !!prevInputModes &&
        !isEqual(this.conversation.context.enabledInputModes, prevInputModes)
      ) {
        // if the message failed, restore the previous input modes
        this.conversation.setContextValue('enabledInputModes', prevInputModes);
      }

      if (resp.code !== 200 && prevPromptMessage) {
        this.emitNewConversationEvent({
          name: 'conversation-message-updated',
          conversationId: this.conversation?.id as string,
          participantId: prevPromptMessage.participant.id,
          messageId: prevPromptMessage.id,
          message: {
            ...prevPromptMessage,
            selectedOptionId: undefined,
          },
        });
      }

      // this needs to happen after we reset the previous input modes,
      // otherwise it might think it's stuck when it's not
      this.checkForStuckConversation(resp, this.conversation);

      return resp;
    } catch (e) {
      const clientErrorResponse = await this.reportAndEmitException(
        `exception sending conversation message: ${(e as Error).message}`,
      );
      return clientErrorResponse;
    }
  };

  sendConversationTyping = async (
    params: SendConversationTypingParams,
  ): Promise<ClientResponse> => {
    if (this.currentChannel === 'chat') {
      return this.chatClient.sendConversationTyping({
        ...params,
        conversationId: this.chatConversationId as string,
      });
    }
    return this.uhClient.sendConversationTyping();
  };

  async streamText(params: StreamTextParams): Promise<StreamTextResponse> {
    return this.uhClient.streamText(params);
  }

  private buildUHConversationSummaryMessage(conversation = this.conversation) {
    if (!conversation) {
      throw new Error('No conversation started');
    }

    const lines = ['Transition to agent:'];

    if (conversation.context.topic) {
      lines.push(`Selected: ${conversation.context.topic}`);
    }

    if (conversation.context.latestQuestion) {
      lines.push(`\nUser message:\n${conversation.context.latestQuestion}`);
    } else {
      const firstSelectionItem = conversation.log.get().find((item) => {
        return (
          item.name === 'conversation-message-added' &&
          item.message.type === 'button-options-conversation-message'
        );
      }) as ConversationLogItemMessageAdded | undefined;

      const msg = firstSelectionItem?.message as
        | ButtonOptionsConversationMessage
        | undefined;
      const firstSelection = msg?.options.find(
        (option) => option.id === msg?.selectedOptionId,
      );

      if (firstSelection) {
        lines.push(`\nUser message:\n${firstSelection.label}`);
      }
    }

    return conversation.buildTextMessage(lines.join('\n'));
  }

  private subscribeToClientStatus(client: Client) {
    this.clientStatusSub?.unsubscribe();
    this.status$.next(client.getStatus());
    this.clientStatusSub = client
      .statusObservable()
      // debounce will also defer updating status from the child client
      // to avoid our base class overwriting the connection status
      .pipe(debounceTime(50))
      .subscribe((s) => {
        this.status$.next(s);
      });
  }

  private incrementAndTrack(
    name: string,
    params: Record<string, string | number> = {},
  ): void {
    const metricParams = {...METRIC_TAGS, ...params};
    this.metrics.increment(name, metricParams);
    this.analytics.track(name, metricParams);
  }

  protected startPlaceholderConversation(
    params: StartConversationParams & {
      conversationId?: string;
      channelConversationId?: string;
      participant: ConversationParticipant;
    },
  ): StartConversationResponse {
    const {participant} = params;

    const {context} = params;
    if (!context?.triggerParams) {
      throw new Error(
        'Error starting placeholder conversation - missing triggerParams',
      );
    }

    const {initialQuery, initialGreeting} = context.triggerParams;
    const timestamp = Date.now();

    const tempMessages: Record<string, ConversationMessage> = {};
    if (initialGreeting) {
      tempMessages['inital-greeting'] = {
        id: 'initial-greeting',
        nonce: 'initial-greeting',
        type: 'text-conversation-message',
        timestamp: timestamp - 200, // we want this to be first in the log
        participant: UniversalHelpUser,
        text: initialGreeting,
      };
    }

    if (initialQuery) {
      tempMessages['initial-query'] = {
        id: 'initial-query',
        nonce: 'initial-query',
        type: 'text-conversation-message',
        timestamp: timestamp + 100,
        participant,
        text: initialQuery as string,
      };
    }

    const conversation = new Conversation(this, {
      id: 'placeholder-conversation',
      user: participant,
      status: ConversationStatus.Active,
      syncStorage: false,
      messages: tempMessages,
      participants: {
        [UniversalHelpUser.id]: UniversalHelpUser,
        [participant.id]: participant,
      },
      context: {
        ...context,
        channel: 'bot',
        enabledInputModes: [],
      },
    });

    // Provide a "loading" state for the placeholder conversation
    conversation.handleEventFromClient({
      id: uuid(),
      name: 'conversation-typing-started',
      conversationId: conversation.id,
      participantId: UniversalHelpUser.id,
      timestamp: Date.now(),
    });

    return {code: 200, data: {conversation}};
  }

  private async reportAndEmitException(
    error: string,
    is5xxErrorCode = true,
  ): Promise<ClientErrorResponse> {
    let clientErrorResponse: ClientErrorResponse;
    const errorTags = {
      ...ERROR_TAGS,
      assistant_error_screen: true,
    };
    if (is5xxErrorCode) {
      clientErrorResponse = await this.error(500, error, errorTags);
    } else {
      const eventId = this.errors.reportError(error, {
        tags: errorTags,
      });

      clientErrorResponse = {
        // this code value satisfies the return type, however, currently all instances of non 5xx errors
        // do not use this return value, they just fire-and-forget.
        code: 400,
        error,
        sentryEventId: eventId,
      };
    }

    this.emitNewEvent({
      name: 'client-exception',
      error,
      sentryEventId: clientErrorResponse.sentryEventId,
    });

    return clientErrorResponse;
  }

  public hasShownInitialGreeting(): boolean {
    return this.initialGreetingShown;
  }

  public markInitialGreetingAsShown(): void {
    this.initialGreetingShown = true;
  }

  private inProgressResponseForRequest(
    responses: InProgressResponse[],
    params: InProgressParams,
  ): InProgressResponse {
    let channelInProgressResponse: InProgressResponse | undefined;
    let chatInProgressResponse: InProgressResponse | undefined;
    let racInProgressResponse: InProgressResponse | undefined;
    const actuallyInProgressResponses = responses.filter((resp) => {
      if ('error' in resp) {
        return false;
      }

      if (
        params.context?.channel &&
        resp.data.channel === params.context?.channel
      ) {
        channelInProgressResponse = resp;
      }

      // only set if is actually in progress, so we can prefer it
      if (resp.data.channel === 'chat' && resp.data.inProgress) {
        chatInProgressResponse = resp;
      }

      // only set if is actually in progress, so we can prefer it
      if (resp.data.channel === 'request_a_call' && resp.data.inProgress) {
        racInProgressResponse = resp;
      }

      return resp.data.inProgress;
    });

    // if the request is asking for a specific channel, prefer it
    if (channelInProgressResponse) {
      return channelInProgressResponse;
    }

    // else if we have an ongoing chat, prefer it regardless of other in progress calls
    if (chatInProgressResponse) {
      return chatInProgressResponse;
    }

    // else if we have an ongoing request a call, prefer it over the bot
    if (racInProgressResponse) {
      return racInProgressResponse;
    }

    if (actuallyInProgressResponses.length > 0) {
      return actuallyInProgressResponses[0];
    }

    return {code: 200, data: {inProgress: false}};
  }

  checkForStuckConversation(
    resp: ClientResponse,
    conversation: Conversation | undefined | null,
  ): void {
    if (resp.code === 400 && !!conversation) {
      const failedRequests =
        this.incrementFailedConversationRequests(conversation);

      if (this.conversationLooksStuck(failedRequests, conversation)) {
        // now, we don't want to unnecessarily halt the conversation
        // with an error screen. for now, let's just get visibility
        this.errors.reportWarning('Conversation looks stuck', {
          tags: ERROR_TAGS,
          extras: {
            conversation_id: conversation.id,
          },
        });
      }
    } else {
      this.clearFailedConversationRequests(conversation);
    }
  }

  private incrementFailedConversationRequests(
    conversation: Conversation,
  ): number {
    const count = this.stuckConversationRequests.get(conversation) ?? 0;
    const newCount = count + 1;
    this.stuckConversationRequests.set(conversation, newCount);
    return newCount;
  }

  private clearFailedConversationRequests(
    conversation: Conversation | undefined | null,
  ) {
    if (conversation) {
      this.stuckConversationRequests.delete(conversation);
    }
  }

  private conversationLooksStuck(
    failedRequests: number,
    conversation: Conversation,
  ): boolean {
    const hasPrompt = conversation.log.get().some((item) => {
      if (item.name !== 'conversation-message-added') {
        return false;
      }
      if (
        ![
          'button-options-conversation-message',
          'select-option-conversation-message',
        ].includes(item.message.type)
      ) {
        return false;
      }

      const message = item.message as
        | ButtonOptionsConversationMessage
        | SelectOptionsConversationMessage;

      // if there's an option message (prompt) with an unselected
      // option, the user can use it to select an option and
      // send a message
      return message.selectedOptionId === undefined;
    });

    if (
      failedRequests > 0 &&
      this.conversationInputIsDisabled(conversation) &&
      !hasPrompt
    ) {
      // if we don't have a prompt and the input is also disabled, that would mean
      // the conversation is probably stuck as the user doesn't have a way of sending
      // a message.
      return true;
    }

    return false;
  }

  private conversationInputIsDisabled(conversation: Conversation): boolean {
    const inputModes = (conversation.context.enabledInputModes ||
      []) as string[];
    const inputEnabled = inputModes.includes('text');
    return !inputEnabled;
  }

  private get featureFlags(): AppFeatureFlags {
    return this.profile.featureFlags;
  }

  isBusy(): boolean {
    if (!this.featureFlags.enableUhPendingInput) {
      return false;
    }

    if (this.currentChannel === 'chat') {
      return false;
    }

    const pending = this.uhClient.isPending();
    const isStreaming = this.isLatestLogItemStreaming();

    return pending || isStreaming;
  }

  private isLatestLogItemStreaming(): boolean {
    const item = this.getLatestLogItem();
    if (this.isActiveInlineDataLogItem(item)) {
      return item.message.inlineData.name === 'stripe_assistant';
    }

    if (
      item &&
      item.name === 'conversation-message-added' &&
      item.message.type === 'custom-part-conversation-message'
    ) {
      const {part} = item.message;
      if (
        part.type === 'progress' ||
        (part.type === 'ai_response' &&
          (part.dynamic === 'if_latest' || part.dynamic === 'active'))
      ) {
        return true;
      }
    }

    return false;
  }

  // delegates to the UH client busyObservable, but also treats streaming requests (stripe_assistant or V2 ai_response parts) as busy
  busyObservable(): Observable<boolean> {
    // of() will be an empty observable if conversation isn't set
    const logObservable = this.conversation?.log.observable() ?? of();
    const streamingObservable = logObservable.pipe(
      // map log changes to if is latest item is a streaming one
      map(() => this.isLatestLogItemStreaming()),
    );

    // if the UH client is busy or we are streaming
    return merge(
      this.uhClient.busyObservable().pipe(startWith(this.uhClient.isPending())),
      streamingObservable.pipe(startWith(this.isLatestLogItemStreaming)),
    ).pipe(map(() => this.isBusy()));
  }

  sendFeedback({
    conversationId,
    responseId,
    feedback,
  }: SendFeedbackParams): Promise<ClientResponse> {
    return this.uhClient.sendFeedback({
      conversationId,
      responseId,
      feedback,
    });
  }
}
